/**
 * Methods to help with dealing with numbers in JavaScript.
 */
import i18n, { SWISS_GERMAN_LOCALE } from "../translations/i18n";

/**
 * Formats an amount as string using selected language (locale of that language).
 */
export const getLocale = () => {
    const lang = i18n.language;
    return lang === SWISS_GERMAN_LOCALE ? "de-CH" : "en-US";
};

export function formatAmount(amount: number | undefined): string {
    if (amount !== undefined) {
        const formatter = new Intl.NumberFormat(getLocale(), {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        return formatter.format(amount);
    }
    return "";
}

export function formatInterestRate(interestRate: any): string {
    if (interestRate !== undefined && interestRate !== null) {
        const formatter = new Intl.NumberFormat(getLocale(), {
            style: "decimal",
            minimumFractionDigits: 3,
            maximumFractionDigits: 3,
        });
        return formatter.format(interestRate);
    }
    return "";
}

/**
 * Converts a number expressed as string to a number.
 */
export function parseNumber(numberString: string) {
    return parseFloat(numberString);
}

export function electronicFormat(iban: string) {
    const NON_ALPHANUM = /[^a-zA-Z0-9]/g;
    return iban.replace(NON_ALPHANUM, "").toUpperCase();
}

/**
 * Formats IBAN given as string to a proper IBAN format.
 */
export function parseIBAN(iban: string) {
    const EVERY_FOUR_CHARS = /(.{4})(?!$)/g;
    const separator = " ";

    return electronicFormat(iban).replace(EVERY_FOUR_CHARS, "$1" + separator);
}

/**
 * Rounds a number on two decimals using default rounding.
 * throws Error in case when a value is not a number.
 */
export function round(num: number) {
    if (isNaN(num)) {
        throw new Error("A number must be given.");
    }
    return Math.round(num * 100) / 100;
}

/**
 * Returns a number for a given number string in a given locale.
 */
export function parseNumberWithLocale(value: string) {
    const example = Intl.NumberFormat(getLocale()).format(parseNumber("1.1"));
    const cleanPattern = new RegExp(`[^-+0-9${example.charAt(1)}]`, "g");
    const cleaned = value.replace(cleanPattern, "");
    const normalized = cleaned.replace(example.charAt(1), ".");

    return parseFloat(normalized);
}
